'use client';

import { useCallback, useState } from 'react';
import { Copy, Code2, FileText } from 'lucide-react';

import { notify } from '@/lib/notifications';
import {
  copyScheduleJsonToClipboard,
  copyScheduleTextToClipboard,
  type ScheduleClipboardResource,
} from '@/lib/scheduleClipboard';
import type { WorkItem } from '@/components/UniversalWorkItems';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ScheduleExportButtonProps {
  items: WorkItem[];
  resources: ScheduleClipboardResource[];
  selectedDate: Date;
  includeEmptyResources?: boolean;
  className: string;
  title?: string;
  fullLabel?: string;
  compactLabel?: string;
  dialogTitle?: string;
  dialogDescription?: string;
}

export default function ScheduleExportButton({
  items,
  resources,
  selectedDate,
  includeEmptyResources = true,
  className,
  title = 'Export schedule',
  fullLabel = 'Export schedule',
  compactLabel = 'Export',
  dialogTitle = 'Export schedule',
  dialogDescription = 'Choose how you want to copy this schedule to the clipboard.',
}: ScheduleExportButtonProps) {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async (format: 'text' | 'json') => {
    try {
      setIsExporting(true);

      if (format === 'json') {
        await copyScheduleJsonToClipboard({
          items,
          resources,
          selectedDate,
          includeEmptyResources,
        });
        notify.success('Schedule JSON copied');
      } else {
        await copyScheduleTextToClipboard({
          items,
          resources,
          selectedDate,
          includeEmptyResources,
        });
        notify.success('Schedule text copied');
      }

      setOpen(false);
    } catch (error) {
      console.error(`[ScheduleExportButton] Failed to copy ${format} schedule:`, error);
      notify.error('Failed to copy schedule');
    } finally {
      setIsExporting(false);
    }
  }, [includeEmptyResources, items, resources, selectedDate]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className}
        title={title}
      >
        <Copy className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{fullLabel}</span>
        <span className="sm:hidden">{compactLabel}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl border-gray-200 bg-white p-0 dark:border-gray-700 dark:bg-gray-900">
          <DialogHeader className="border-b border-gray-100 px-5 py-4 text-left dark:border-gray-800">
            <DialogTitle className="text-base text-gray-900 dark:text-gray-100">{dialogTitle}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 px-5 py-4">
            <button
              type="button"
              onClick={() => { void handleExport('text'); }}
              disabled={isExporting}
              className="flex w-full items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-left text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-900/40"
            >
              <FileText className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Copy as text</span>
                <span className="block text-xs text-blue-600/80 dark:text-blue-300/80">
                  Copies the readable day schedule format already used across the board views.
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => { void handleExport('json'); }}
              disabled={isExporting}
              className="flex w-full items-start gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-left text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300 dark:hover:bg-violet-900/40"
            >
              <Code2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">Copy as JSON</span>
                <span className="block text-xs text-violet-600/80 dark:text-violet-300/80">
                  Copies the same schedule data in a structured machine-friendly format.
                </span>
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
